import { useState, useEffect, useCallback } from 'react';
import { Save, X, Eye, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { FileText } from 'lucide-react';
import { Confirm, useNotify } from 'react-admin';
import TemplateDataDetail from './TemplateDataDetail.tsx';
import TemplateImport from './TemplateImport.tsx';

declare const process: {
	env: {
 		REACT_APP_API_URL?: string;
 	};
};

export interface COATemplateItem {
	id?: number;
	batchNumber?: string;
	parameter: string;
	classification: string;
	testMethod: string;
	uom: string;
}

export interface COATemplate {
	id: string;
	batchNumber: string;
	productDescription: string;
	specNumber: string;
	sampleSubmissionRefNumber: string;
	manufactureDate: string;
	expiryDate: string;
	description?: string;
	preparedBy?: string;
	preparedByTitle?: string;
	preparedBySignature?: string;
	preparedByDate?: string;
	releaseBy?: string;
	releaseByTitle?: string;
	releaseBySignature?: string;
	releaseByDate?: string;
	items: COATemplateItem[];
}

type TemplateListFormData = Pick<COATemplate,
	'batchNumber' |
	'productDescription' |
	'specNumber' |
	'manufactureDate' |
	'expiryDate'
>;

interface TemplateDataListProps {
	templates: COATemplate[];
	onUpdate: (templates: COATemplate[]) => void;
}

export default function TemplateDataList({ templates = [], onUpdate }: TemplateDataListProps) {
	const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
	const [isAdding, setIsAdding] = useState(false);
	const [editingId, setEditingId] = useState<string | null>(null);
	const [formData, setFormData] = useState<TemplateListFormData>({
		batchNumber: '',
		productDescription: '',
		specNumber: '',
		manufactureDate: '',
		expiryDate: '',
	});
	const [editingTemplateFull, setEditingTemplateFull] = useState<COATemplate | null>(null);

	const [templatesState, setTemplatesState] = useState<COATemplate[]>(templates || []);
	const [page, setPage] = useState(1);
	const [perPage, setPerPage] = useState(10);
	const [batchNumberQuery, setBatchNumberQuery] = useState('');
	const [productNameQuery, setProductNameQuery] = useState('');
	const [listFilter, setListFilter] = useState<{ batchNumber?: string; productDescription?: string } | null>(null);
	const [totalCount, setTotalCount] = useState<number>(0);
	const [loadingList, setLoadingList] = useState(false);
	const notify = useNotify();
	const [saving, setSaving] = useState(false);
	const [detailTemplate, setDetailTemplate] = useState<COATemplate | null>(null);
	const [loadingDetail, setLoadingDetail] = useState(false);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
	const fetchTemplates = useCallback(async (p: number, pp: number, filterObj: any = null) => {
		setLoadingList(true);
		try {
			const start = (p - 1) * pp;
			const end = start + pp - 1;
			const params = new URLSearchParams();
			params.set('range', `[${start},${end}]`);
			params.set('perPage', String(pp));
			if (filterObj) params.set('filter', JSON.stringify(filterObj));

			const token = localStorage.getItem('token');
			const res = await fetch(`${API_URL}/COATemplates?${params.toString()}`, {
				method: 'GET',
				headers: {
					...(token ? { Authorization: `Bearer ${token}` } : {})
				}
			});

			if (!res.ok) throw new Error(`HTTP ${res.status}`);

			const list = await res.json();
			const contentRange = res.headers.get('Content-Range') || '';
			const m = /\/(\d+)$/.exec(contentRange);
			const total = m ? parseInt(m[1], 10) : list.length;

			setTemplatesState(list || []);
			setTotalCount(total);
			setPage(p);
			setPerPage(pp);
		} catch (err) {
			console.error('Failed to load templates', err);
		} finally {
			setLoadingList(false);
		}
	}, [API_URL]);

	useEffect(() => {
		fetchTemplates(page, perPage, listFilter);
	}, [fetchTemplates, page, perPage, listFilter]);

	useEffect(() => {
		const debounceTimer = window.setTimeout(() => {
			const batchNumber = batchNumberQuery.trim();
			const productDescription = productNameQuery.trim();
			const nextFilter: { batchNumber?: string; productDescription?: string } = {};

			if (batchNumber) nextFilter.batchNumber = batchNumber;
			if (productDescription) nextFilter.productDescription = productDescription;

			setPage(1);
			setListFilter(Object.keys(nextFilter).length > 0 ? nextFilter : null);
		}, 300);

		return () => window.clearTimeout(debounceTimer);
	}, [batchNumberQuery, productNameQuery]);

	const handleEditTemplate = (template: COATemplate) => {
		setIsAdding(true);
		setEditingId(template.id);
		setEditingTemplateFull(template);
		setFormData({
			batchNumber: template.batchNumber || '',
			productDescription: template.productDescription || '',
			specNumber: template.specNumber || '',
			manufactureDate: template.manufactureDate ? template.manufactureDate.slice(0, 10) : '',
			expiryDate: template.expiryDate ? template.expiryDate.slice(0, 10) : '',
		});
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	const fetchTemplateById = useCallback(async (id: string): Promise<COATemplate | null> => {
		const token = localStorage.getItem('token');
		const res = await fetch(`${API_URL}/COATemplates/${id}`, {
			method: 'GET',
			headers: {
				...(token ? { Authorization: `Bearer ${token}` } : {}),
			},
		});

		if (!res.ok) return null;
		const template = await res.json();
		return template as COATemplate;
	}, [API_URL]);

	const handleOpenDetail = async (id: string) => {
		setLoadingDetail(true);
		try {
			const template = await fetchTemplateById(id);
			if (!template) {
				notify('Failed to load COA template details', { type: 'error' });
				return;
			}
			setDetailTemplate(template);
		} finally {
			setLoadingDetail(false);
		}
	};



	const handleSave = useCallback(async () => {
		if (!(formData.batchNumber && formData.productDescription && formData.specNumber && formData.manufactureDate && formData.expiryDate)) {
			notify('Please fill all required template fields', { type: 'warning' });
			return;
		}

		setSaving(true);
		try {
			const basicFieldsDto = {
				BatchNumber: formData.batchNumber,
				ProductDescription: formData.productDescription,
				SpecNumber: formData.specNumber,
				ManufactureDate: formData.manufactureDate,
				ExpiryDate: formData.expiryDate,
			};

			const createDto = {
				...basicFieldsDto,
				Items: []
			};

			const token = localStorage.getItem('token');

			let res: Response;
			if (editingId) {
				res = await fetch(`${API_URL}/COATemplates/${editingId}/basic-fields`, {
					method: 'PATCH',
					headers: {
						'Content-Type': 'application/json',
						...(token ? { Authorization: `Bearer ${token}` } : {})
					},
					body: JSON.stringify(basicFieldsDto)
				});
			} else {
				res = await fetch(`${API_URL}/COATemplates`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						...(token ? { Authorization: `Bearer ${token}` } : {})
					},
					body: JSON.stringify(createDto)
				});
			}

			if (!res.ok) {
				if (res.status === 409) {
					const payload = await res.json().catch(() => null);
					const message = payload?.message || `Batch number ${formData.batchNumber} already exists.`;
					notify(message, { type: 'error' });
					return;
				}

				const text = await res.text();
				throw new Error(text || `HTTP ${res.status}`);
			}

			const returned = await res.json().catch(() => null);

			if (editingId) {
				if (typeof onUpdate === 'function') {
					// prefer server-returned full object; otherwise merge formData into local full template
					const updated = (returned && returned.id) ? returned : { ...(editingTemplateFull ?? { id: editingId }), ...formData };
					onUpdate(templates.map(s => s.id === editingId ? updated : s));
				}
				setEditingId(null);
				setEditingTemplateFull(null);
			} else {
				const newTemplate: COATemplate = {
					id: (returned && returned.id) ? String(returned.id) : Date.now().toString(),
					sampleSubmissionRefNumber: '',
					...formData
					,
					items: []
				};
				if (typeof onUpdate === 'function') {
					onUpdate([...templates, newTemplate]);
				}
			}

			notify('Template saved successfully', { type: 'success' });
			try {
				fetchTemplates(page, perPage, listFilter);
			} catch (e) {
			}
			setIsAdding(false);
			setFormData({
				batchNumber: '',
				productDescription: '',
				specNumber: '',
				manufactureDate: '',
				expiryDate: '',
			});
		} catch (error: any) {
			console.error('Save template error', error);
			notify(`Save template failed: ${error?.message || 'Unknown error'}`, { type: 'error' });
		} finally {
			setSaving(false);
		}
	}, [API_URL, formData, editingId, templates, onUpdate, notify, fetchTemplates, page, perPage, listFilter, editingTemplateFull]);

	const handleDeleteClick = (id: string) => {
		setPendingDeleteId(id);
		setDeleteConfirmOpen(true);
	};

	const handleConfirmDelete = () => {
		const id = pendingDeleteId;
		if (!id) return;

		(async () => {
				try {
					const token = localStorage.getItem('token');
					const res = await fetch(`${API_URL}/COATemplates/${id}`, {
						method: 'DELETE',
						headers: {
							...(token ? { Authorization: `Bearer ${token}` } : {})
						}
					});
					if (!res.ok) throw new Error(`HTTP ${res.status}`);
					const nextTotal = Math.max(0, totalCount - 1);
					const nextTotalPages = Math.max(1, Math.ceil(nextTotal / perPage));
					const targetPage = Math.min(page, nextTotalPages);
					if (targetPage !== page) {
						setPage(targetPage);
					} else {
						fetchTemplates(targetPage, perPage, listFilter);
					}
					if (typeof onUpdate === 'function') onUpdate(templates.filter(template => template.id !== id));
					notify('COA template deleted successfully', { type: 'success' });
				} catch (err: any) {
					console.error('Delete spec error', err);
					notify(`Delete failed: ${err?.message || 'Unknown error'}`, { type: 'error' });
				} finally {
					setDeleteConfirmOpen(false);
					setPendingDeleteId(null);
				}
		})();
	};


	const handleCancel = () => {
		setIsAdding(false);
		setEditingId(null);
		setEditingTemplateFull(null);
		setFormData({
			batchNumber: '',
			productDescription: '',
			specNumber: '',
			manufactureDate: '',
			expiryDate: '',
		});
	};

	if (detailTemplate) {
		return (
			<TemplateDataDetail
				template={detailTemplate}
				onBack={() => setDetailTemplate(null)}
				onUpdated={(updatedTemplate) => {
					setDetailTemplate(updatedTemplate);
					setTemplatesState((current) => current.map((template) => (template.id === updatedTemplate.id ? updatedTemplate : template)));
					if (typeof onUpdate === 'function') {
						onUpdate(templates.map((template) => (template.id === updatedTemplate.id ? updatedTemplate : template)));
					}
				}}
			/>
		);
	}

	return (
		<div className="space-y-6" style={{ margin: 20 }}>
			<div className="flex items-center justify-between">
				<div>
					<h2 className="text-2xl mb-1">COA Template Management</h2>
					<p className="text-sm text-gray-500">Import analytical files and maintain COA templates</p>
				</div>
				{!isAdding && (
					<div className="flex items-center gap-2">
						<TemplateImport
							apiUrl={API_URL}
							onImported={() => fetchTemplates(page, perPage, listFilter)}
						/>
					</div>
				)}
			</div>

			{isAdding && (
				<div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-6">
					<div className="flex items-center justify-between pb-4 border-b">
						<h3 className="text-xl">{editingId ? 'Edit COA Template' : 'New COA Template'}</h3>
						<span className="text-sm text-gray-500">Fill in COA template master information</span>
					</div>
					<div className="grid grid-cols-2 gap-4">
						<div className="flex items-center gap-3">
							<Label htmlFor="batchNumber" className="min-w-[96px] text-sm text-gray-700">Batch Number</Label>
							<Input id="batchNumber" value={formData.batchNumber} onChange={(e) => setFormData({ ...formData, batchNumber: e.target.value })} placeholder="e.g. BN-20260418-01" className="flex-1" />
						</div>
						<div className="flex items-center gap-3">
							<Label htmlFor="productDescription" className="min-w-[96px] text-sm text-gray-700">Product Name</Label>
							<Input id="productDescription" value={formData.productDescription} onChange={(e) => setFormData({ ...formData, productDescription: e.target.value })} placeholder="e.g. Microcrystalline Cellulose" className="flex-1" />
						</div>
						<div>
							<Label htmlFor="specNumber" className="text-sm text-gray-700">Spec Number</Label>
							<Input id="specNumber" value={formData.specNumber} onChange={(e) => setFormData({ ...formData, specNumber: e.target.value })} placeholder="e.g. SPEC-001" className="mt-1" />
						</div>
						<div>
							<Label htmlFor="manufactureDate" className="text-sm text-gray-700">Manufacture Date</Label>
							<Input id="manufactureDate" type="date" value={formData.manufactureDate ? formData.manufactureDate.slice(0, 10) : ''} onChange={(e) => setFormData({ ...formData, manufactureDate: e.target.value })} className="mt-1" />
						</div>
						<div>
							<Label htmlFor="expiryDate" className="text-sm text-gray-700">Expiry Date</Label>
							<Input id="expiryDate" type="date" value={formData.expiryDate ? formData.expiryDate.slice(0, 10) : ''} onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })} className="mt-1" />
						</div>
						</div>

					<div className="flex items-center justify-end gap-3 pt-4 border-t">
						<Button variant="outline" onClick={handleCancel} disabled={saving}>
							<X className="size-4 mr-2" />
							Cancel
						</Button>
						<Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
							<Save className="size-4 mr-2" />
							{saving ? 'Saving...' : 'Save'}
						</Button>
					</div>
				</div>
			)}

			{!isAdding && (
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
					<div className="border-b border-gray-200 bg-slate-50 px-4 py-3">
						<div className="flex flex-wrap items-center gap-3">
							<div className="flex min-w-[280px] flex-1 max-w-md items-center gap-3">
								<Label htmlFor="list-product-name" className="min-w-[96px] shrink-0 text-sm text-gray-700">Product Name</Label>
								<Input
									id="list-product-name"
									value={productNameQuery}
									onChange={(e) => setProductNameQuery(e.target.value)}
									placeholder="Search product name"
									className="w-full bg-white"
								/>
							</div>
							<div className="flex min-w-[280px] flex-1 max-w-md items-center gap-3">
								<Label htmlFor="list-batch-number" className="min-w-[96px] shrink-0 text-sm text-gray-700">Batch Number</Label>
								<Input
									id="list-batch-number"
									value={batchNumberQuery}
									onChange={(e) => setBatchNumberQuery(e.target.value)}
									placeholder="Search batch number"
									className="w-full bg-white"
								/>
							</div>
						</div>
					</div>
					<div>
						<table className="w-full table-fixed">
							<thead>
								<tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
									<th className="w-[20%] px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Product Name</th>
									<th className="w-[10%] px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Batch Number</th>
									<th className="w-[13%] px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Manufacture Date</th>
									<th className="w-[15%] px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Spec Number</th>
									<th className="w-[13%] px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Expiry Date</th>
                                    
									<th className="w-[18%] px-4 py-3 text-center text-xs text-gray-600 uppercase tracking-wider">Actions</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-100">
								{templatesState.map((spec) => (
									<tr key={spec.id} className="hover:bg-gray-50 transition-colors">
										<td className="px-4 py-3 text-sm text-gray-900"><span className="block truncate" title={spec.productDescription}>{spec.productDescription}</span></td>
										<td className="px-4 py-3 text-sm text-gray-900"><span className="block truncate" title={spec.batchNumber}>{spec.batchNumber}</span></td>
										<td className="px-4 py-3 text-sm text-gray-600">{new Date(spec.manufactureDate).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })}</td>
										<td className="px-4 py-3 text-sm text-gray-900"><span className="block truncate" title={spec.specNumber}>{spec.specNumber}</span></td>
										<td className="px-4 py-3 text-sm text-gray-600">{new Date(spec.expiryDate).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })}</td>
                                        
										<td className="px-4 py-3 text-right">
											<div className="flex gap-2 justify-end">
												<Button variant="ghost" size="sm" onClick={() => handleEditTemplate(spec)} className="text-amber-600 hover:text-amber-700 hover:bg-amber-50" title="Edit">
													<Pencil className="size-4" />
												</Button>
												<Button variant="ghost" size="sm" onClick={() => handleOpenDetail(spec.id)} className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50" disabled={loadingDetail} title="Details">
													<Eye className="size-4" />
												</Button>
												<Button variant="ghost" size="sm" onClick={() => handleDeleteClick(spec.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" title="Delete">
													<Trash2 className="size-4" />
												</Button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{!loadingList && templatesState.length === 0 && !isAdding && (
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
					<FileText className="size-16 mx-auto text-gray-300 mb-4" />
					<h3 className="text-lg text-gray-600 mb-2">No COA templates yet</h3>
					<p className="text-sm text-gray-400 mb-6">Please import COA template data from Excel.</p>
				</div>
			)}

			<Confirm
				isOpen={deleteConfirmOpen}
				loading={false}
				title="Confirm Delete"
				content="Are you sure you want to delete this COA template record?"
				onConfirm={handleConfirmDelete}
				onClose={() => {
					setDeleteConfirmOpen(false);
					setPendingDeleteId(null);
				}}
			/>
		</div>
	);
}
